import React, { ElementType, ReactNode, forwardRef, useRef, useCallback, useState, useLayoutEffect } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../type-utils';

const SCROLL_DEBOUNCE_MS = 16; // ~60fps

export interface HintScrollProps {
  children: ReactNode;
  direction?: 'horizontal' | 'vertical';
  shadowColor?: string;
  shadowSize?: number;
  shadowOpacity?: number;
  threshold?: number;
  disabled?: boolean;
  borderRadius?: string | number;
  'data-testid'?: string;
}

type HintScrollPolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithRef<C, HintScrollProps>;

type HintScrollComponent = <C extends ElementType = 'div'>(props: HintScrollPolymorphicProps<C>) => React.ReactNode;

const HintScrollImpl = forwardRef(
  <C extends ElementType = 'div'>(
    {
      children,
      as,
      direction = 'vertical',
      shadowColor,
      shadowSize = 12,
      shadowOpacity = 0.15,
      threshold = 5,
      disabled = false,
      borderRadius,
      ...rest
    }: HintScrollPolymorphicProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || 'div';
    const isVertical = direction === 'vertical';

    const containerRef = useRef<HTMLElement | null>(null);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [shadowState, setShadowState] = useState({ showStartShadow: false, showEndShadow: false });

    const handleScroll = useCallback(() => {
      const element = containerRef.current;
      if (!element || disabled) {
        setShadowState({ showStartShadow: false, showEndShadow: false });
        return;
      }

      const scrollPos = isVertical ? element.scrollTop : element.scrollLeft;
      const maxScroll = isVertical
        ? element.scrollHeight - element.clientHeight
        : element.scrollWidth - element.clientWidth;

      const showStartShadow = scrollPos > threshold;
      const showEndShadow = scrollPos < maxScroll - threshold;

      setShadowState({ showStartShadow, showEndShadow });
    }, [disabled, isVertical, threshold]);

    const handleScrollDebounced = useCallback(() => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(handleScroll, SCROLL_DEBOUNCE_MS);
    }, [handleScroll]);

    // Setup scroll listener and initial check
    useLayoutEffect(() => {
      const element = containerRef.current;
      if (!element) return;

      // Initial value set
      handleScroll();

      // Add scroll listener
      element.addEventListener('scroll', handleScrollDebounced, { passive: true });

      // Cleanup
      return () => {
        element.removeEventListener('scroll', handleScrollDebounced);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, [handleScroll, handleScrollDebounced]);

    // Responsive check on resize
    useLayoutEffect(() => {
      const element = containerRef.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver(handleScroll);

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [handleScroll]);

    return (
      <div css={[wrapperStyle, { borderRadius: borderRadius || 'inherit' }]} {...rest}>
        {/* Start shadow (top for vertical, left for horizontal) */}
        {!disabled && (
          <div
            css={[
              baseShadowStyle,
              {
                opacity: shadowState.showStartShadow ? shadowOpacity : 0,
                top: 0,
                left: 0,
                [isVertical ? 'right' : 'bottom']: 0,
                [isVertical ? 'height' : 'width']: `${shadowSize}px`,
                background: `linear-gradient(to ${isVertical ? 'bottom' : 'right'}, ${
                  shadowColor || theme.colors.black
                }, transparent)`,
              },
            ]}
            aria-hidden='true'
          />
        )}

        {/* End shadow (bottom for vertical, right for horizontal) */}
        {!disabled && (
          <div
            css={[
              baseShadowStyle,
              {
                opacity: shadowState.showEndShadow ? shadowOpacity : 0,
                bottom: 0,
                right: 0,
                [isVertical ? 'left' : 'top']: 0,
                [isVertical ? 'height' : 'width']: `${shadowSize}px`,
                background: `linear-gradient(to ${isVertical ? 'top' : 'left'}, ${
                  shadowColor || theme.colors.black
                }, transparent)`,
              },
            ]}
            aria-hidden='true'
          />
        )}

        {/* Scrollable content */}
        <Component
          ref={(node: HTMLElement | null) => {
            containerRef.current = node;
            if (typeof ref === 'function') {
              ref(node as any);
            } else if (ref) {
              (ref as React.MutableRefObject<any>).current = node;
            }
          }}
          css={scrollableStyle}
        >
          {children}
        </Component>
      </div>
    );
  }
);

HintScrollImpl.displayName = 'HintScroll';

export const HintScroll = HintScrollImpl as HintScrollComponent;

const wrapperStyle = css({
  position: 'relative',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
});

const scrollableStyle = css({
  position: 'relative',
  overflow: 'auto',
  height: '100%',
  width: '100%',
  borderRadius: 'inherit',
});

const baseShadowStyle = css({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: theme.zIndex.overlay,
  transition: 'opacity 0.2s ease-in-out',
});
